import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Icon, Text } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';

import IntegrationStaticSelect from './IntegrationStaticSelect';
import './IntegrationMultiUserSearch.scss';

function compareInitialState(itemsSelected, initialItems) {
    if (itemsSelected.length !== initialItems.length) return false;

    for (let i = 0; i < itemsSelected.length; i++) {
        const found = initialItems.some((item) => item.id === itemsSelected[i].id);
        if (!found) return false;
    }

    return true;
}

const IntegrationMultiUserSearch = memo(
    ({
        data = [],
        hintText,
        itemsSelectedProps = [],
        maxItems,
        noItemsSelectedLiteral,
        numItemsSelectedLiteral,
        onSave,
    }) => {
        const [itemsSelected, setItemsSelected] = useState(itemsSelectedProps);
        const [initialItems] = useState(itemsSelectedProps);
        const [disableSelect, setDisableSelect] = useState(
            Boolean(maxItems && itemsSelectedProps.length === maxItems),
        );

        const checkItems = useCallback(
            (currentItems, item) => {
                if (maxItems && maxItems <= currentItems.length) return false;
                if (!currentItems.length) return true;
                return !currentItems.some(
                    (selected) => selected.id.toString() === item.id.toString(),
                );
            },
            [maxItems],
        );

        const handleSelect = useCallback(
            (item) => {
                if (!item) return;

                setItemsSelected((currentItems) => {
                    if (!checkItems(currentItems, item)) {
                        return currentItems;
                    }

                    const nextItems = [...currentItems, item];
                    setDisableSelect(Boolean(maxItems && nextItems.length === maxItems));
                    return nextItems;
                });
            },
            [checkItems, maxItems],
        );

        const deleteItemSelected = useCallback((id) => {
            setItemsSelected((currentItems) => {
                const nextItems = currentItems.filter((item) => item.id !== id);
                setDisableSelect(false);
                return nextItems;
            });
        }, []);

        const handleSave = useCallback(() => {
            onSave?.(itemsSelected);
        }, [itemsSelected, onSave]);

        const disableButton = useMemo(
            () => compareInitialState(itemsSelected, initialItems),
            [initialItems, itemsSelected],
        );

        const itemsSelectedNumber = useMemo(() => {
            if (maxItems && maxItems > 0) {
                return `${itemsSelected.length}/${maxItems}`;
            }
            return itemsSelected.length;
        }, [itemsSelected.length, maxItems]);

        const listItemsSelected = useMemo(() => {
            if (!itemsSelected.length) {
                return (
                    <div className="no-items-selected">
                        <div className="no-items-selected-center">
                            <Text type="body">{getLiteral(noItemsSelectedLiteral)}</Text>
                        </div>
                    </div>
                );
            }

            return (
                <div>
                    <Text as="div" className="num-items-selected" medium type="subtitle1">
                        {getLiteral(numItemsSelectedLiteral).replace('%@', itemsSelectedNumber)}
                    </Text>
                    <div className="integration-multi-user-list">
                        {itemsSelected.map((item) => (
                            <div key={item.id} className="item-container">
                                <div className="inside-item-container item">{item.value}</div>
                                <div className="inside-item-container icon">
                                    <Icon
                                        className="integration-multi-user-remove"
                                        name="closeSmall"
                                        onClick={() => deleteItemSelected(item.id)}
                                        size="large"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }, [
            deleteItemSelected,
            itemsSelected,
            itemsSelectedNumber,
            noItemsSelectedLiteral,
            numItemsSelectedLiteral,
        ]);

        return (
            <div className="integration-multi-user-search multifuzzysearch-component-container">
                <div className="multi-fuzzy-search-container">
                    <div className="search-users-container">
                        <div className="value-list-container">
                            <IntegrationStaticSelect
                                className="integration-multi-user-select"
                                data={data}
                                disabled={disableSelect}
                                hintText={hintText || 'placeholder_select'}
                                onSelect={handleSelect}
                                searchOnly
                            />
                        </div>
                    </div>
                    {listItemsSelected}
                </div>
                <div className="btn-save-container">
                    <Button isDisabled={disableButton} onClick={handleSave} type="primary">
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

IntegrationMultiUserSearch.displayName = 'IntegrationMultiUserSearch';

IntegrationMultiUserSearch.propTypes = {
    data: PropTypes.array.isRequired,
    hintText: PropTypes.string,
    itemsSelectedProps: PropTypes.array,
    maxItems: PropTypes.number,
    noItemsSelectedLiteral: PropTypes.string,
    numItemsSelectedLiteral: PropTypes.string,
    onSave: PropTypes.func.isRequired,
};

export default IntegrationMultiUserSearch;
